import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Navbar from '../components/layout/Navbar';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import parkingApi from '../services/parkingApi';
import './BusinessSignupPage.css';

const BUSINESS_TYPES = [
  'Restaurant / Food Service',
  'Retail Store',
  'Salon / Beauty',
  'Auto Repair',
  'Construction / Trades',
  'Healthcare / Medical',
  'Professional Services',
  'Event / Entertainment',
  'Other',
];

const step1Schema = Yup.object({
  businessName: Yup.string().required('Business name is required'),
  businessType: Yup.string().required('Business type is required'),
  phone: Yup.string()
    .matches(/^[0-9()\-+\s]{7,15}$/, 'Enter a valid phone number')
    .required('Phone number is required'),
  city: Yup.string().required('City is required'),
});

const step2Schema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Enter a valid email').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

const initialValues = {
  businessName: '',
  businessType: '',
  phone: '',
  city: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

const BusinessSignupPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleStep1 = (values) => {
    setFormData((prev) => ({ ...prev, ...values }));
    setStep(2);
  };

  const handleStep2 = (values) => {
    setFormData((prev) => ({ ...prev, ...values }));
    setStep(3);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      if (!isSupabaseConfigured) {
        throw new Error('Signup is not available — backend is not configured.');
      }

      // 1. Create the owner's login.
      const { data: authData, error: authErr } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });
      if (authErr) throw new Error(authErr.message);

      // 2. Provision the business + its first lot, linking the owner account.
      const vendor = await parkingApi.vendorSignup({
        business_name: formData.businessName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        owner_name: `${formData.firstName} ${formData.lastName}`.trim(),
        auth_user_id: authData?.user?.id || null,
      });

      if (authData?.session?.access_token) {
        localStorage.setItem('authToken', authData.session.access_token);
      }

      // Send the owner to their new lot's enforcement console.
      navigate('/enforcement', { state: { slug: vendor.slug } });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <Navbar />
      <div className="signup-wrapper">
        <div className="signup-box">
          <div className="signup-header">
            <h1>Create Your Business Account</h1>
            <p>Join RGV businesses accepting payments with ease</p>
          </div>

          {/* Progress */}
          <div className="signup-steps">
            {['Business Info', 'Owner Info', 'Confirm'].map((label, i) => (
              <div className={`signup-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`} key={label}>
                <div className="signup-step-circle">{step > i + 1 ? '✓' : i + 1}</div>
                <span>{label}</span>
              </div>
            ))}
          </div>

          {/* Step 1 */}
          {step === 1 && (
            <Formik initialValues={formData} validationSchema={step1Schema} onSubmit={handleStep1}>
              {({ isSubmitting }) => (
                <Form className="signup-form">
                  <div className="form-group">
                    <label className="form-label">Business Name *</label>
                    <Field name="businessName" className="form-input" placeholder="e.g. Maria's Flowers" />
                    <ErrorMessage name="businessName" component="div" className="form-error" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Business Type *</label>
                    <Field as="select" name="businessType" className="form-select">
                      <option value="">Select type...</option>
                      {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </Field>
                    <ErrorMessage name="businessType" component="div" className="form-error" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone Number *</label>
                      <Field name="phone" className="form-input" placeholder="(956) 555-0100" />
                      <ErrorMessage name="phone" component="div" className="form-error" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">City *</label>
                      <Field name="city" className="form-input" placeholder="McAllen" />
                      <ErrorMessage name="city" component="div" className="form-error" />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg signup-submit">
                    Continue →
                  </button>
                </Form>
              )}
            </Formik>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <Formik initialValues={formData} validationSchema={step2Schema} onSubmit={handleStep2}>
              {() => (
                <Form className="signup-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">First Name *</label>
                      <Field name="firstName" className="form-input" placeholder="Maria" />
                      <ErrorMessage name="firstName" component="div" className="form-error" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name *</label>
                      <Field name="lastName" className="form-input" placeholder="Garcia" />
                      <ErrorMessage name="lastName" component="div" className="form-error" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <Field name="email" type="email" className="form-input" placeholder="maria@mybusiness.com" />
                    <ErrorMessage name="email" component="div" className="form-error" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Password *</label>
                    <Field name="password" type="password" className="form-input" placeholder="At least 8 characters" />
                    <ErrorMessage name="password" component="div" className="form-error" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm Password *</label>
                    <Field name="confirmPassword" type="password" className="form-input" placeholder="Repeat your password" />
                    <ErrorMessage name="confirmPassword" component="div" className="form-error" />
                  </div>
                  <div className="signup-nav">
                    <button type="button" className="btn btn-outline" onClick={() => setStep(1)}>← Back</button>
                    <button type="submit" className="btn btn-primary btn-lg">Continue →</button>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {/* Step 3: Review & Confirm */}
          {step === 3 && (
            <div className="signup-review">
              <div className="review-section">
                <h3>Business Information</h3>
                <div className="review-grid">
                  <div><span>Business Name</span><strong>{formData.businessName}</strong></div>
                  <div><span>Type</span><strong>{formData.businessType}</strong></div>
                  <div><span>Phone</span><strong>{formData.phone}</strong></div>
                  <div><span>City</span><strong>{formData.city}</strong></div>
                </div>
              </div>
              <div className="review-section">
                <h3>Owner Information</h3>
                <div className="review-grid">
                  <div><span>Name</span><strong>{formData.firstName} {formData.lastName}</strong></div>
                  <div><span>Email</span><strong>{formData.email}</strong></div>
                </div>
              </div>
              {error && <div className="alert alert-error">{error}</div>}
              <div className="signup-nav">
                <button className="btn btn-outline" onClick={() => setStep(2)}>← Back</button>
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? <><span className="spinner"></span> Creating Account...</> : 'Create Account ✓'}
                </button>
              </div>
              <p className="signup-terms">
                By creating an account you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          )}

          <p className="signup-login">
            Already have an account? <Link to="/admin/signin">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BusinessSignupPage;
